import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, Grid, IconButton, Link, Typography } from "@mui/material";
import { format, isValid, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useEffect, useMemo, useState } from "react";
import { FinancialService, IListInvoice } from "../../../shared/services/api/financial/FinancialService";
import { globalMaskDocument } from "../../../shared/utils";

interface ModalDetailInvoiceProps {
	rowInvoice: IListInvoice;
	isOpenModal: boolean;
	onCloseModal: () => void;
}

const formatDateOnly = (value?: string | null) => {
	if (!value) return "-";
	const trimmed = String(value).trim();
	if (!trimmed) return "-";

	const dateOnly = trimmed.length >= 10 ? trimmed.slice(0, 10) : trimmed;
	const parsedDate = parseISO(dateOnly);

	if (!isValid(parsedDate)) return "-";
	return format(parsedDate, "dd/MM/yyyy", { locale: ptBR });
};

const formatCurrency = (value?: string | number | null) => {
	const numericValue = Number(value ?? 0);
	if (!Number.isFinite(numericValue) || numericValue === 0) return "R$ 0,00";
	return numericValue.toLocaleString("pt-br", { style: "currency", currency: "BRL" });
};

const translateStatus = (status?: string) => {
	switch (status) {
		case "open":
			return "Aberto";
		case "paid":
			return "Pago";
		case "pending":
			return "Faturado";
		case "canceled":
			return "Cancelado";
		default:
			return status || "-";
	}
};

const statusChipStyles = (status?: string) => {
	switch (status) {
		case "paid":
			return { color: "#148f77", background: "#d9f5ef" };
		case "open":
			return { color: "#3761eb", background: "#e0ecff" };
		case "pending":
			return { color: "#8f9196", background: "#efefef" };
		case "canceled":
			return { color: "#d73a49", background: "#fdebec" };
		default:
			return { color: "#4b5563", background: "#f3f4f6" };
	}
};

const SectionTitle = ({ title, subtitle }: { title: string; subtitle: string }) => (
	<Box mb={1.5}>
		<Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#111827" }}>
			{title}
		</Typography>
		<Typography variant="body2" sx={{ color: "#6b7280" }}>
			{subtitle}
		</Typography>
	</Box>
);

const DetailItem = ({ label, value }: { label: string; value?: string }) => (
	<Box sx={{ py: 0.75 }}>
		<Typography variant="caption" sx={{ color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.3 }}>
			{label}
		</Typography>
		<Typography variant="body2" sx={{ color: "#111827", fontWeight: 600, wordBreak: "break-word" }}>
			{value || "-"}
		</Typography>
	</Box>
);

export const ModalDetailInvoice: React.FC<ModalDetailInvoiceProps> = ({ rowInvoice, isOpenModal, onCloseModal }) => {
	const [detail, setDetail] = useState<IListInvoice | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);

	useEffect(() => {
		if (!isOpenModal) return;

		setIsLoading(true);
		setLoadError(null);

		FinancialService.getInvoiceById(rowInvoice.id)
			.then((result) => {
				if (result instanceof Error) {
					setLoadError("Não foi possível carregar os detalhes do boleto.");
					return;
				}

				setDetail(result);
			})
			.finally(() => setIsLoading(false));
	}, [isOpenModal, rowInvoice.id]);

	const mergedDetail = useMemo(() => {
		if (!detail) return rowInvoice;
		return {
			...rowInvoice,
			...detail,
			client: {
				...rowInvoice.client,
				...detail.client,
			},
		};
	}, [detail, rowInvoice]);

	const currentStatusStyles = statusChipStyles(mergedDetail.status);
	const invoiceLink = mergedDetail.url || mergedDetail.summaryUrl || mergedDetail.sicoobPdfBoleto || "";
	const installmentsLabel = String(mergedDetail.installmentsNumber ?? mergedDetail.installments_number ?? "-");

	return (
		<Dialog open={isOpenModal} onClose={onCloseModal} fullWidth maxWidth="md">
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
				<Typography variant="h6" color="primary">
					Detalhes do boleto
				</Typography>
				<IconButton onClick={onCloseModal} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				{isLoading && (
					<Box display="flex" justifyContent="center" py={6}>
						<CircularProgress />
					</Box>
				)}

				{loadError && !isLoading && <Alert severity="error">{loadError}</Alert>}

				{!isLoading && !loadError && (
					<>
						<Box
							sx={{
								background: "linear-gradient(135deg, rgba(1,112,179,0.10) 0%, rgba(1,112,179,0.03) 100%)",
								border: "1px solid rgba(1,112,179,0.12)",
								borderRadius: "16px",
								p: { xs: 2, md: 2.5 },
								mb: 3,
							}}
						>
							<Box display="flex" justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} flexDirection={{ xs: "column", md: "row" }} gap={2}>
								<Box>
									<Typography variant="h6" sx={{ fontWeight: 700, color: "#111827" }}>
										{mergedDetail.client?.name || "-"}
									</Typography>
									<Typography variant="body2" sx={{ color: "#6b7280" }}>
										{globalMaskDocument(mergedDetail.client?.document || "")} | {mergedDetail.client?.email || "-"}
									</Typography>
								</Box>
								<Box display="flex" alignItems={{ xs: "flex-start", md: "center" }} gap={1.5} flexDirection={{ xs: "column", md: "row" }}>
									<Chip
										label={translateStatus(mergedDetail.status)}
										sx={{
											color: currentStatusStyles.color,
											background: currentStatusStyles.background,
											fontWeight: 700,
										}}
									/>
									<Box sx={{ px: 1.75, py: 1, borderRadius: "999px", bgcolor: "primary.main", color: "#fff", fontWeight: 700, minWidth: 170, textAlign: "center" }}>
										{formatCurrency(mergedDetail.value)}
									</Box>
								</Box>
							</Box>

							<Grid container spacing={2} sx={{ mt: 1.5 }}>
								<Grid item xs={12} md={3}>
									<DetailItem label="Vencimento" value={formatDateOnly(mergedDetail.dueDate)} />
								</Grid>
								<Grid item xs={12} md={3}>
									<DetailItem label="Pagamento" value={formatDateOnly(mergedDetail.paymentDate)} />
								</Grid>
								<Grid item xs={12} md={3}>
									<DetailItem label="Recebimento" value={formatDateOnly(mergedDetail.creditDate)} />
								</Grid>
								<Grid item xs={12} md={3}>
									<DetailItem label="Referência" value={mergedDetail.reference || "-"} />
								</Grid>
							</Grid>
						</Box>

						<Grid container spacing={3}>
							<Grid item xs={12} md={6}>
								<SectionTitle title="Dados do boleto" subtitle="Identificação e canais do título" />
								<DetailItem label="ID" value={mergedDetail.id} />
								<DetailItem label="Platform ID" value={mergedDetail.platformId || "-"} />
								<DetailItem label="Nosso número" value={mergedDetail.sicoobSeuNumero || "-"} />
								<DetailItem label="Gateway" value={mergedDetail.gateway || "-"} />
								<DetailItem label="Parcelas" value={installmentsLabel} />
								<DetailItem label="Linha digitável" value={mergedDetail.sicoobLinhaDigitavel || "-"} />
								<DetailItem label="Código de barras" value={mergedDetail.sicoobCodigoBarras || "-"} />
							</Grid>

							<Grid item xs={12} md={6}>
								<SectionTitle title="Valores" subtitle="Resumo financeiro do boleto" />
								<DetailItem label="Valor nominal" value={formatCurrency(mergedDetail.value)} />
								<DetailItem label="Desconto" value={formatCurrency(mergedDetail.discount)} />
								<DetailItem label="Acréscimo" value={formatCurrency(mergedDetail.addition)} />
								<DetailItem label="Valor pago" value={formatCurrency(mergedDetail.amountPaid)} />
								<DetailItem label="Valor recebido" value={formatCurrency(mergedDetail.creditAmount)} />
								<DetailItem label="Taxas da plataforma" value={formatCurrency(mergedDetail.platformTaxes)} />
							</Grid>

							<Grid item xs={12} md={6}>
								<SectionTitle title="Cliente" subtitle="Dados básicos do emissor do boleto" />
								<DetailItem label="Nome" value={mergedDetail.client?.name || "-"} />
								<DetailItem label="Documento" value={globalMaskDocument(mergedDetail.client?.document || "")} />
								<DetailItem label="E-mail" value={mergedDetail.client?.email || "-"} />
								<DetailItem label="Status do cliente" value={mergedDetail.client?.status || "-"} />
							</Grid>

							<Grid item xs={12} md={6}>
								<SectionTitle title="Ocorrências" subtitle="Datas operacionais e eventual cancelamento" />
								<DetailItem label="Criado em" value={formatDateOnly(mergedDetail.createdAt)} />
								<DetailItem label="Atualizado em" value={formatDateOnly(mergedDetail.updatedAt)} />
								<DetailItem label="Cancelado em" value={formatDateOnly(mergedDetail.canceledAt)} />
								<DetailItem label="Tipo de cancelamento" value={mergedDetail.canceledType || "-"} />
								<DetailItem label="Motivo do cancelamento" value={mergedDetail.canceledReason || "-"} />
								<DetailItem label="Usuário do cancelamento" value={mergedDetail.canceledUser || "-"} />
							</Grid>
						</Grid>

						{invoiceLink && (
							<Box mt={3} display="flex" justifyContent="flex-end">
								<Button
									variant="outlined"
									endIcon={<OpenInNewIcon />}
									component={Link}
									href={invoiceLink}
									target="_blank"
									rel="noreferrer"
									underline="none"
								>
									Abrir boleto
								</Button>
							</Box>
						)}
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};